import {
  Box,
  RoutineOccasionInlineStatusCircleRelay,
  TouchableBox,
} from "arkh";
import React, { useCallback } from "react";
import { Platform } from "react-native";
import { graphql, useFragment, useMutation } from "react-relay";
import { WidgetTeamScheduleInlineRoutineListItemMutation } from "./__generated__/WidgetTeamScheduleInlineRoutineListItemMutation.graphql";
import { WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion$key } from "./__generated__/WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion.graphql";
import * as Haptics from "expo-haptics";
import { WidgetTeamScheduleRoutineListItemContent } from "./WidgetTeamScheduleRoutineListItemContent";
import { WidgetTeamScheduleRoutineIssueIndicator } from "./WidgetTeamScheduleRoutineIssueIndicator";

export function WidgetTeamScheduleInlineRoutineListItem(props: {
  onPress?(routineId: string, sequenceNumber: number): void;
  inlineRoutineOccasion: WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion$key;
  status: "active" | "soon" | "none";
  freq: "DAILY" | "WEEKLY" | "MONTHLY";
  size: "COMPACT" | "DEFAULT" | "LARGE";
}) {
  const inlineRoutineOccasion = useFragment(
    graphql`
      fragment WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion on TeamScheduleInlineRoutineOccasion {
        id
        taskExecution {
          id
          task {
            id
          }
          sectionId
          routineOccasion {
            id
            sequenceNumber
            ...WidgetTeamScheduleRoutineListItemContent_RoutineOccasion
            ...WidgetTeamScheduleRoutineIssueIndicator_routineOccasion
            ...RoutineOccasionStatusCircleInline_RoutineOccasion
          }
          routine {
            id
            ...WidgetTeamScheduleRoutineListItemContent_Routine
          }
          checkValue
        }
      }
    `,
    props.inlineRoutineOccasion
  );

  const [setCheckValue] =
    useMutation<WidgetTeamScheduleInlineRoutineListItemMutation>(
      graphql`
        mutation WidgetTeamScheduleInlineRoutineListItemMutation(
          $routineId: ID!
          $sequenceNumber: Int!
          $sectionId: ID!
          $taskId: ID!
          $value: TaskSentiment
        ) @raw_response_type {
          routine(id: $routineId) {
            occasion(sequenceNumber: $sequenceNumber) {
              section(id: $sectionId) {
                setCheck(taskId: $taskId, sentiment: $value) {
                  id
                  checkValue
                  isCompleted
                  routineOccasion {
                    status(calculateNoTasks: true)
                  }
                }
              }
            }
          }
        }
      `
    );

  const onToggle = useCallback(() => {
    if (Platform.OS === "ios") {
      Haptics.impactAsync();
    }
    const val =
      inlineRoutineOccasion.taskExecution.checkValue === "POSITIVE"
        ? null
        : "POSITIVE";
    if (!inlineRoutineOccasion.taskExecution.routine) {
      return;
    }

    setCheckValue({
      variables: {
        routineId: inlineRoutineOccasion.taskExecution.routine.id,
        sequenceNumber:
          inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
        sectionId: inlineRoutineOccasion.taskExecution.sectionId,
        taskId: inlineRoutineOccasion.taskExecution.task.id,
        value: val,
      },
      optimisticResponse:
        val === "POSITIVE"
          ? {
              routine: {
                occasion: {
                  section: {
                    setCheck: {
                      checkValue: val,
                      id: inlineRoutineOccasion.taskExecution.id,
                      isCompleted: true,
                      routineOccasion: {
                        id: inlineRoutineOccasion.taskExecution.routineOccasion
                          .id,
                        status: "COMPLETED",
                      },
                    },
                  },
                },
              },
            }
          : undefined,
    });
  }, [
    inlineRoutineOccasion.taskExecution.checkValue,
    inlineRoutineOccasion.taskExecution.id,
    inlineRoutineOccasion.taskExecution.routine,
    inlineRoutineOccasion.taskExecution.routineOccasion.id,
    inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
    inlineRoutineOccasion.taskExecution.sectionId,
    inlineRoutineOccasion.taskExecution.task.id,
    setCheckValue,
  ]);

  const { onPress } = props;

  const op = useCallback(() => {
    if (inlineRoutineOccasion.taskExecution.routine) {
      onPress?.(
        inlineRoutineOccasion.taskExecution.routine?.id,
        inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber
      );
    }
  }, [
    inlineRoutineOccasion.taskExecution.routine,
    inlineRoutineOccasion.taskExecution.routineOccasion.sequenceNumber,
    onPress,
  ]);

  return (
    <TouchableBox
      style={{
        flex: 1,
        flexDirection: "row",
        justifyContent: "space-between",
        alignContent: "center",
        alignItems: "center",
        height: "100%",
      }}
      onPress={op}
      disabled={!props.onPress}
      spaceRight="medium"
    >
      <WidgetTeamScheduleRoutineListItemContent
        status={props.status}
        routine={inlineRoutineOccasion.taskExecution.routine!}
        size={props.size}
        routineOccasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        freq={props.freq}
      />
      <TouchableBox onPress={onToggle}>
        <WidgetTeamScheduleRoutineIssueIndicator
          routineOccasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        />
        <RoutineOccasionInlineStatusCircleRelay
          circleSize="medium"
          occasion={inlineRoutineOccasion.taskExecution.routineOccasion}
        />
      </TouchableBox>
    </TouchableBox>
  );
}
